// @flow
import React, { Component } from 'react';
import ClaimedWebsiteMark from 'app/common/react/components/profile/ProfileHeader/ClaimedWebsiteMark';
import Dot from 'app/common/react/components/experimental/Dot';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import PrettyNumber from 'app/common/react/i18n/PrettyNumber';
import Testable from 'app/common/react/ui/Testable';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import urlUtil from 'app/common/lib/urlUtil';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { prettifyUrl } from 'app/packages/url/formatUrl';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { smartTruncate } from 'app/common/lib/react/strUtils';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Column, Heading, Text } from 'gestalt';

type HOCProps = {|
  requestContext: RequestContextType,
|};

type OwnProps = {|
  isOwnProfile: boolean,
  user: ProfileUser,
|};

type Props = {|
  ...OwnProps,
  ...HOCProps,
|};

const formatDisplayUrl = (url: string) => {
  const prettyUrl = prettifyUrl(url);
  return urlUtil.punycode_decode(prettyUrl);
};

/**
 * Renders the row containing the user's information.
 * i.e. user's full name, follower count, following count, website, about description,
 * and avatar.
 */
export class UserInfo extends Component<Props> {
  shouldShowImpression = (): boolean => {
    const {
      user: { impressum_url: impressumUrl },
      requestContext: { country },
    } = this.props;

    // For legal reasons we need to sometimes show the impressum URL in certain countries
    // http://www.allthingsgerman.net/blog/blogging/blogging-in-germany-the-impressum/#.WdV56ROPJE4
    return !!(impressumUrl && ['DE', 'AT', 'CH'].includes(country.toUpperCase()));
  };

  render() {
    const { isOwnProfile, user, requestContext } = this.props;
    const isAuthenticated = requestContext.isAuthenticated;

    const showImpressum = this.shouldShowImpression();

    const hasWebsiteSeparator = user.website_url && showImpressum;
    const aboutText = user.about && smartTruncate(user.about, 160);

    // Follower counts
    const followerText = interpolateNamedTemplate(
      i18n.ngettext(
        '{{count}} follower',
        '{{count}} followers',
        user.follower_count,
        'label for n followers',
      ),
      { count: <PrettyNumber value={user.follower_count || 0} key="following-count" /> },
    );

    // Following counts
    const followingText = interpolateNamedTemplate(
      i18n.ngettext(
        '{{count}} following',
        '{{count}} following',
        user.following_count,
        'label for n following users',
      ),
      { count: <PrettyNumber value={user.following_count || 0} key="follower-count" /> },
    );

    const profileParityEnabled = new SingleTreatmentExp(
      requestContext,
      'web_all_profiles_follow_parity',
    ).enabledWithNoActivation();

    const linkAddress = profileParityEnabled ? '_' : '';

    const followingOrFollowerLink = (param) => (
      <Box display="flex" direction="row">
        <NavigateLink
          to={`/${user.username}/${linkAddress}${param}/`}
          className="underlineLink"
          noFollow={!isAuthenticated}
        >
          <Box display="flex">
            <Text color="darkGray" size="sm" weight="bold">
              {param === 'following' ? followingText : followerText}
            </Text>
          </Box>
        </NavigateLink>
        {param === 'followers' && <Dot />}
      </Box>
    );

    const followingOrFollowerText = (param) => (
      <Box display="flex">
        <Text size="sm" color="darkGray">
          {param === 'following' ? followingText : followerText}
        </Text>
        {param === 'followers' && <Dot />}
      </Box>
    );

    return (
      <Box alignItems="center" direction="row" display="flex" flex="grow" justifyContent="between">
        <Column span={12}>
          <Box display="flex" direction="column" paddingX={2} justifyContent={'start'}>
            <Box dangerouslySetInlineStyle={{ __style: { marginLeft: '-1px' } }}>
              <Heading size="sm">{user.full_name}</Heading>
            </Box>
            <Box display="flex" direction="row" alignItems="center">
              <Box display="flex" direction="row" alignItems="center" marginBottom={1}>
                <Box>
                  {isAuthenticated
                    ? followingOrFollowerLink('followers')
                    : followingOrFollowerText('followers')}
                </Box>
                <Box>
                  {isAuthenticated
                    ? followingOrFollowerLink('following')
                    : followingOrFollowerText('following')}
                </Box>
              </Box>
            </Box>
            {hasWebsiteSeparator && (
              <Box display="flex" direction="row">
                <Dot />
                <NavigateLink to={user.impressum_url} useNewTab className="underlineLink">
                  <Text color="darkGray" inline size="sm" weight="bold">
                    {i18n._('Imprint')}
                  </Text>
                </NavigateLink>
              </Box>
            )}
            {user.website_url && (
              <Box display="flex" direction="row" marginTop={!isOwnProfile ? 2 : 0}>
                {// $FlowFixMe: new errors found from flow 0.96 upgrade
                isAuthenticated && user.domain_verified && <ClaimedWebsiteMark />}
                <NavigateLink to={user.website_url} className="underlineLink" external>
                  <Box>
                    <Text color="darkGray" size="sm" truncate weight="bold">
                      {formatDisplayUrl(user.website_url)}
                    </Text>
                  </Box>
                </NavigateLink>
              </Box>
            )}
            <Box marginBottom={3} marginTop={!user.website_url && !isOwnProfile ? 2 : 0}>
              {user.location && !aboutText && <Text size="sm">{user.location}</Text>}
              {!user.location && aboutText && <Text size="sm">{aboutText}</Text>}
              {user.location &&
                aboutText && (
                  <Box>
                    <Testable id="userAbout">
                      <Text size="sm">
                        {user.location}
                        &nbsp;
                        {' · '}
                        &nbsp;
                        {aboutText}
                      </Text>
                    </Testable>
                  </Box>
                )}
            </Box>
          </Box>
        </Column>
      </Box>
    );
  }
}

export default withRequestContext(UserInfo);
